<script setup lang="ts">
  import { computed } from 'vue';
  import { OS_OF_TITLE_LABELS } from '../../lib/record-config-options';
  import RecordConfigFieldCombobox from './RecordConfigFieldCombobox.vue';
  import type { MatrixField } from '../../model/matrix-field.types';

  const INDEXER_LABEL_ID = 'record-config-indexing-indexer-label';
  const FIELD_PLACEHOLDER = 'Selecione um campo';

  const props = defineProps<{
    indexerFieldName: string | null;
    titleNames: Array<string | null>;
    fields: MatrixField[];
    isLoading: boolean;
    disabled?: boolean;
  }>();

  const emit = defineEmits<{
    'update:indexerFieldName': [value: string | null];
    'update:titleNames': [value: Array<string | null>];
  }>();

  const titleRows = computed(() =>
    OS_OF_TITLE_LABELS.map((label, index) => ({
      label,
      labelId: `record-config-indexing-title-${index + 1}-label`,
      value: props.titleNames[index] ?? null,
    }))
  );

  const indexerOptions = computed(() =>
    optionsExcluding(
      titleRows.value.map((row) => row.value),
      props.indexerFieldName
    )
  );

  function optionsExcluding(others: Array<string | null>, ownValue: string | null): MatrixField[] {
    const takenElsewhere = new Set(others.filter((name) => name !== null));
    return props.fields.filter(
      (field) => field.name === ownValue || !takenElsewhere.has(field.name)
    );
  }

  function titleOptions(index: number): MatrixField[] {
    const others = titleRows.value
      .filter((_, rowIndex) => rowIndex !== index)
      .map((row) => row.value);
    return optionsExcluding([props.indexerFieldName, ...others], titleRows.value[index].value);
  }

  function setTitle(index: number, value: string | null): void {
    const next = titleRows.value.map((row) => row.value);
    next[index] = value;
    emit('update:titleNames', next);
  }
</script>

<template>
  <div class="flex flex-col gap-3" data-testid="record-config-os-of-indexing">
    <span :id="INDEXER_LABEL_ID" class="text-sm font-semibold text-gray-07">Campo indexador:</span>
    <RecordConfigFieldCombobox
      :model-value="indexerFieldName"
      :options="indexerOptions"
      :placeholder="FIELD_PLACEHOLDER"
      :is-loading="isLoading"
      :aria-labelledby="INDEXER_LABEL_ID"
      :disabled="disabled"
      @update:model-value="emit('update:indexerFieldName', $event)"
    />

    <span class="mt-2 text-sm font-semibold text-gray-07">
      Selecione os títulos a serem preenchidos
    </span>
    <div
      v-for="(row, index) in titleRows"
      :key="row.labelId"
      class="flex flex-wrap items-center gap-x-3 gap-y-2"
      data-testid="record-config-os-of-title-row"
    >
      <span :id="row.labelId" class="w-20 shrink-0 text-sm text-gray-07">{{ row.label }}</span>
      <RecordConfigFieldCombobox
        :model-value="row.value"
        :options="titleOptions(index)"
        :placeholder="FIELD_PLACEHOLDER"
        :is-loading="isLoading"
        :aria-labelledby="row.labelId"
        :disabled="disabled"
        @update:model-value="setTitle(index, $event)"
      />
    </div>
  </div>
</template>
